import React, { useState } from 'react';
import config from './config';
import CompanyDisambiguationModal from './components/CompanyDisambiguationModal';

function CitationAnalysisPage({ userUsage }) {
  const [companyName, setCompanyName] = useState('');
  const [companyData, setCompanyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDisambiguation, setShowDisambiguation] = useState(false);
  const [disambiguationMatches, setDisambiguationMatches] = useState([]);

  const handleDisambiguationSelect = (selectedCompany) => {
    setCompanyName(selectedCompany);
    setShowDisambiguation(false);
    searchCompanyDirect(selectedCompany);
  };

  const searchCompanyDirect = async (exactName) => {
    setLoading(true);
    setError('');
    setCompanyData(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/api/citations/company/${encodeURIComponent(exactName)}?limit=20&token=${token}`);
      const data = await response.json();

      if (data.error) {
        setError(data.error);
      } else {
        setCompanyData(data);
      }
    } catch (err) {
      setError('Failed to fetch citation data');
    }

    setLoading(false);
  };


  const searchCompany = async () => {
    if (!companyName.trim()) return;

    try {
      const disambigResponse = await fetch(`${config.API_URL}/api/disambiguate-company?query=${encodeURIComponent(companyName)}`);
      const disambigData = await disambigResponse.json();
      
      if (disambigData.needs_disambiguation && disambigData.matches.length > 1) {
        setDisambiguationMatches(disambigData.matches);
        setShowDisambiguation(true);
        return;
      }
      
      const exactName = disambigData.matches.length === 1 ? disambigData.matches[0].assignee : companyName;
      searchCompanyDirect(exactName);
    } catch (err) {
      searchCompanyDirect(companyName);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchCompany();
    }
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Back Button */}
      <button
        onClick={() => window.location.reload()}
        style={{
          marginBottom: '16px',
          padding: '8px 16px',
          background: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px'
        }}
      >
        ← Back to Search
      </button>

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#1a237e', marginBottom: '8px' }}>
          Company Citation Analysis
        </h1>
        <p style={{ color: '#666', fontSize: '14px' }}>
          Analyze patent citation relationships between companies. See who cites whom in the patent landscape.
        </p>
        {userUsage?.citations && (
          <div style={{ fontSize: '13px', color: '#5e778a', marginTop: '12px' }}>
            {userUsage.citations.limit < 9999 ? (
              <>Citation analyses: {userUsage.citations.remaining} of {userUsage.citations.limit} remaining today</>
            ) : (
              <span style={{ color: '#10b981' }}>Unlimited citation analyses</span>
            )}
          </div>
        )}
      </div>

      {/* Search Box */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter company name (e.g., Apple, Google, Samsung)"
          style={{
            flex: 1,
            padding: '12px 16px',
            fontSize: '16px',
            border: '2px solid #1976d2',
            borderRadius: '8px',
            outline: 'none'
          }}
        />
        <button
          onClick={searchCompany}
          disabled={loading}
          style={{
            padding: '12px 32px',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: loading ? 'wait' : 'pointer'
          }}
        >
          {loading ? 'Analyzing...' : 'Analyze'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '16px', background: '#ffebee', borderRadius: '8px', color: '#c62828', marginBottom: '24px' }}>
          {error}
        </div>
      )}

      {/* Results */}
      {companyData && (
        <div>
          {/* Stats Overview */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: '#e3f2fd', padding: '24px', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#1565c0', marginBottom: '8px' }}>Company</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0d47a1' }}>{companyData.assignee}</div>
            </div>
            <div style={{ background: '#fff3e0', padding: '24px', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#e65100', marginBottom: '8px' }}>Outgoing Citations</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e65100' }}>
                {companyData.total_outgoing_citations?.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Patents this company cites</div>
            </div>
            <div style={{ background: '#e8f5e9', padding: '24px', borderRadius: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: '#2e7d32', marginBottom: '8px' }}>Incoming Citations</div>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2e7d32' }}>
                {companyData.total_incoming_citations?.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>Patents citing this company</div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* Companies This Company Cites */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #ddd', overflow: 'hidden' }}>
              <div style={{ background: '#ff9800', padding: '16px', color: 'white' }}>
                <h3 style={{ margin: 0, fontSize: '18px' }}>
                  Companies "{companyData.assignee}" Cites Most
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
                  Prior art references in their patents
                </p>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {companyData.cites_companies?.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: '1px solid #eee',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: '#ff9800',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {idx + 1}
                      </span>
                      <span style={{ fontWeight: idx < 3 ? 'bold' : 'normal' }}>{item.company}</span>
                    </div>
                    <span style={{
                      background: '#fff3e0',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#e65100'
                    }}>
                      {item.citation_count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Companies That Cite This Company */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #ddd', overflow: 'hidden' }}>
              <div style={{ background: '#4caf50', padding: '16px', color: 'white' }}>
                <h3 style={{ margin: 0, fontSize: '18px' }}>
                  Companies That Cite "{companyData.assignee}"
                </h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
                  References to their patents in other companies' filings
                </p>
              </div>
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                {companyData.cited_by_companies?.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 16px',
                      borderBottom: '1px solid #eee',
                      background: idx % 2 === 0 ? '#fff' : '#fafafa'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: '#4caf50',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {idx + 1}
                      </span>
                      <span style={{ fontWeight: idx < 3 ? 'bold' : 'normal' }}>{item.company}</span>
                    </div>
                    <span style={{
                      background: '#e8f5e9',
                      padding: '4px 12px',
                      borderRadius: '16px',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      color: '#2e7d32'
                    }}>
                      {item.citation_count.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Insight Box */}
          <div style={{ marginTop: '24px', padding: '20px', background: '#f3e5f5', borderRadius: '12px', border: '1px solid #ce93d8' }}>
            <h4 style={{ margin: '0 0 12px 0', color: '#7b1fa2' }}>Citation Insights</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', fontSize: '14px' }}>
              <div>
                <strong>Citation Ratio:</strong>{' '}
                {companyData.total_incoming_citations > 0 
                  ? (companyData.total_outgoing_citations / companyData.total_incoming_citations).toFixed(2)
                  : 'N/A'
                }
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {companyData.total_outgoing_citations > companyData.total_incoming_citations
                    ? 'Cites more than cited (net referencer)'
                    : 'Cited more than cites (net influencer)'
                  }
                </div>
              </div>
              <div>
                <strong>Top Relationship:</strong>{' '}
                {companyData.cites_companies?.[0]?.company || 'N/A'}
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Most frequently cited company
                </div>
              </div>
              <div>
                <strong>Top Citing:</strong>{' '}
                {companyData.cited_by_companies?.[0]?.company || 'N/A'}
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Company that cites most often
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!companyData && !loading && !error && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#666' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
          <h3>Search for a Company</h3>
          <p>Enter a company name above to see their patent citation relationships.</p>
          <p style={{ fontSize: '13px', marginTop: '16px' }}>
            Try: Apple, Google, Samsung, Microsoft, Amazon, Tesla, IBM
          </p>
        </div>
      )}

      <CompanyDisambiguationModal
        show={showDisambiguation}
        onClose={() => setShowDisambiguation(false)}
        query={companyName}
        matches={disambiguationMatches}
        onSelect={handleDisambiguationSelect}
        loading={false}
      />
    </div>
  );
}

export default CitationAnalysisPage;